import { useState, useEffect } from "react";
import {
  getMlQueries,
  getMlQueriesStatistics,
} from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { RiDownloadLine } from "react-icons/ri";
import {
  PageOrientation,
  Document,
  Footer,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  BorderStyle,
  AlignmentType,
  convertInchesToTwip,
  HeadingLevel,
  TableLayoutType,
  VerticalAlign,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { getTenantById } from "../../../../shared/services/apis";
import ChatMarkdown from "../../../../shared/ui/chatmarkdown/ChatMarkdown";

import ReactMakrdown from "react-markdown";

interface MlQuery {
  id: string;
  tenantId: string;
  query: string;
  description: string;
  riskType: string;
  type: string;
  category: "KYB" | "RISK_PARAMETERS";
  response: string | null;
  responseStatus: string;
  successAt: string | null;
  failureAt: string | null;
  partialAt: string | null;
  pendingAt: string | null;
  inProgressAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  priority?: number;
  tenant: {
    id: string;
    name: string;
    brand: {
      id: string;
      name: string;
    };
  };
}

interface Statistics {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
}

interface MlQueriesProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
  category: "KYB" | "RISK_PARAMETERS" | "BANK_RISK_SCORE";
  title?: string;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

export const MlQueries = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
  category,
  title,
}: MlQueriesProps) => {
  const [queries, setQueries] = useState<MlQuery[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedType] = useState<string>("ALL");
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  // Helper function to safely parse response
  const parseResponse = (response: string | null) => {
    if (!response) return null;

    try {
      const parsed = JSON.parse(response);
      // Ensure parsed object has expected structure
      return {
        response: parsed.response || parsed,
        sources: parsed.sources || [],
      };
    } catch {
      // If parsing fails, treat as plain text response (this is expected behavior)
      return { response: response, sources: [] };
    }
  };

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const displayTitle =
    title ||
    (category === "KYB"
      ? "KYC & KYB"
      : category === "BANK_RISK_SCORE"
      ? "Bank Risk Score"
      : "Risk Parameters");

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [queriesResponse, statsResponse] = await Promise.all([
          getMlQueries(tenantId, tenantServiceId, tenantServiceOptionId, {
            category,
            ...(selectedType !== "ALL" && { type: selectedType }),
            limit: 100,
          }),
          getMlQueriesStatistics(
            tenantId,
            tenantServiceId,
            tenantServiceOptionId,
            category
          ),
        ]);

        // Sort queries by priority (ascending) - lower priority number comes first
        const sortedQueries = (queriesResponse.data || []).sort(
          (a: MlQuery, b: MlQuery) => {
            const priorityA = a.priority ?? 999;
            const priorityB = b.priority ?? 999;
            return priorityA - priorityB;
          }
        );

        setQueries(sortedQueries);
        setStatistics(statsResponse.data);
      } catch (err) {
        console.error(`Failed to fetch ${displayTitle} data:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    tenantId,
    tenantServiceId,
    tenantServiceOptionId,
    category,
    selectedType,
    displayTitle,
  ]);

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };

      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text(displayTitle, pageWidth / 2, 20, { align: "center" });

      // Subtitle
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      // Statistics
      if (statistics && statistics.total > 0) {
        doc.setFontSize(9);
        doc.text(`Total Records: ${statistics.total}`, 14, 36);
      }

      // Prepare table data
      const tableHeaders =
        category === "KYB"
          ? ["S.No.", "Question", "Answer"]
          : ["Type of Risk", "SubRisk", "Response"];

      const tableData = queries.map((query, idx) => {
        const parsedResponse = parseResponse(query.response);

        let response = parsedResponse?.response || "N/A";

        // Add source page numbers to response
        if (parsedResponse?.sources && parsedResponse.sources.length > 0) {
          const sourcePages = parsedResponse.sources
            .map((src: any) => `[${src.page_number || "Source"}]`)
            .join(", ");
          response += ` ${sourcePages}`;
        }

        if (category === "KYB") {
          return [`${idx + 1}`, query.query, response];
        } else {
          return [query.riskType, query.description, response];
        }
      });

      // Generate table
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 42,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 4,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: "bold",
          halign: "left",
        },
        bodyStyles: {
          textColor: [55, 65, 81],
        },
        columnStyles: {
          0: { cellWidth: category === "KYB" ? 15 : 35 },
          1: { cellWidth: category === "KYB" ? 70 : 40 },
          2: { cellWidth: "auto" },
        },
        margin: { top: 42, left: 14, right: 14 },
        didDrawPage: (data) => {
          // Footer
          addWatermark(data.pageNumber);
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 10, {
            align: "center",
          });
        },
      });

      // Save PDF
      const fileName = `${displayTitle.replace(
        /\s+/g,
        "_"
      )}_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      // Helper function to convert base64 to Uint8Array
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      // Header paragraphs
      const headerParagraphs: Paragraph[] = [];

      headerParagraphs.push(
        new Paragraph({
          text: displayTitle,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200, before: 200 },
        })
      );

      // Subtitle with DATE AND TIME
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      headerParagraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on ${currentDateTime}`,
              size: 20,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      if (statistics && statistics.total > 0) {
        headerParagraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Records: `,
                size: 20,
                color: "333333",
              }),
              new TextRun({
                text: `${statistics.total}`,
                size: 20,
                bold: true,
                color: "1F4788",
              }),
            ],
            spacing: { after: 600, before: 200 },
          })
        );
      }

      headerParagraphs.push(
        new Paragraph({
          border: {
            bottom: {
              color: "CCCCCC",
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6,
            },
          },
          spacing: { after: 400 },
        })
      );

      const colWidths = category === "KYB" ? [8, 37, 55] : [20, 25, 55];

      const tableHeaders =
        category === "KYB"
          ? ["S.No.", "Question", "Answer"]
          : ["Type of Risk", "SubRisk", "Response"];

      const headerCells = tableHeaders.map((text, idx) => {
        return new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: text,
                  bold: true,
                  size: 20,
                  color: "FFFFFF",
                }),
              ],
              alignment: AlignmentType.LEFT,
              spacing: { after: 100, before: 100 },
            }),
          ],
          shading: { fill: "4472C4" },
          width: { size: colWidths[idx], type: WidthType.PERCENTAGE },
          margins: {
            top: convertInchesToTwip(0.08),
            bottom: convertInchesToTwip(0.08),
            left: convertInchesToTwip(0.08),
            right: convertInchesToTwip(0.08),
          },
          verticalAlign: VerticalAlign.CENTER,
        });
      });

      const tableRows = queries.map((query, idx) => {
        const parsedResponse = query.response
          ? JSON.parse(query.response as string)
          : null;

        let response = parsedResponse?.response || "N/A";

        if (parsedResponse?.sources && parsedResponse.sources.length > 0) {
          const sourcePages = parsedResponse.sources
            .map((src: any) => `[${src.page_number || "Source"}]`)
            .join(", ");
          response += ` ${sourcePages}`;
        }

        const cellValues =
          category === "KYB"
            ? [`${idx + 1}`, query.query, response]
            : [query.riskType, query.description, response];

        const rowColor = idx % 2 === 0 ? "FFFFFF" : "F8F9FA";

        return new TableRow({
          children: cellValues.map((val, i) => {
            const isFirstColumn = i === 0;
            const isQuestionColumn = i === 1;

            return new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: val || "N/A",
                      size: 18,
                      color: "333333",
                      font: "Calibri",
                      ...(isFirstColumn && { bold: true }),
                      ...(isQuestionColumn &&
                        category === "KYB" && { bold: true }),
                    }),
                  ],
                  alignment: isFirstColumn
                    ? AlignmentType.CENTER
                    : AlignmentType.LEFT,
                  spacing: { after: 80, before: 80, line: 276 },
                }),
              ],
              width: { size: colWidths[i], type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
              shading: { fill: rowColor },
              verticalAlign: VerticalAlign.TOP,
            });
          }),
        });
      });

      const table = new Table({
        rows: [
          new TableRow({
            children: headerCells,
            tableHeader: true,
            cantSplit: true,
          }),
          ...tableRows,
        ],
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
        columnWidths: colWidths.map((w) => w * 100),
        layout: TableLayoutType.AUTOFIT,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          insideHorizontal: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
          insideVertical: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
        },
      });

      // Create footer with logo in right bottom corner
      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60, // Smaller size for footer
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      // Add generation text to footer
      footerChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Document generated by ${
                queries[0]?.tenant?.brand?.name || "System"
              }`,
              size: 16,
              color: "999999",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 100 },
        })
      );

      const doc = new Document({
        creator: queries[0]?.tenant?.brand?.name || "Credit Analysis System",
        title: displayTitle,
        description: `${displayTitle} report generated on ${new Date().toLocaleDateString()}`,
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri",
                size: 22,
              },
            },
          },
        },
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: convertInchesToTwip(0.75),
                  right: convertInchesToTwip(0.75),
                },
                size: {
                  width: convertInchesToTwip(8.5),
                  height: convertInchesToTwip(11),
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: [...headerParagraphs, table],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `${displayTitle.replace(
        /\s+/g,
        "_"
      )}_${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl bg-white bg-opacity-60 backdrop-blur-md p-6 max-w-4xl mt-0 2xl:mt-20 mx-auto shadow-md animate-pulse">
        <div className="flex gap-2 flex-wrap mb-4 px-2">
          {["filter-1", "filter-2", "filter-3", "filter-4", "filter-5"].map(
            (id) => (
              <div key={id} className="h-6 w-20 bg-gray-200 rounded-full" />
            )
          )}
        </div>
        <div className="space-y-4">
          {["row-1", "row-2", "row-3", "row-4", "row-5"].map((id) => (
            <div key={id} className="p-4 bg-gray-100 rounded-xl">
              <div className="h-4 w-3/4 bg-gray-300 rounded mb-2" />
              <div className="h-3 w-1/2 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FDFDFD] py-6 rounded-2xl shadow-md border border-gray-300 mt-0 2xl:mt-20 max-w-4xl mx-auto px-6 mb-30">
      {/* Header with Download Buttons */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">{title}</h1>
          {statistics && statistics.total > 0 && (
            <p className="text-sm text-gray-600">
              {statistics.total} {category}
            </p>
          )}
        </div>

        {queries.length > 0 && (
          <div className="flex gap-2">
            {/* Word Download Button */}
            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" /> Word
                </>
              )}
            </button>

            {/* PDF Download Button */}
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" /> PDF
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Queries Table */}
      <div className="mb-5 mt-6 border-t border-gray-200 pt-4">
        {queries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">No queries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  {category === "KYB" && (
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm w-1/25">
                      S.No.
                    </th>
                  )}
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm w-1/4">
                    {category === "KYB" ? "Question" : "Type of Risk"}
                  </th>
                  {category === "RISK_PARAMETERS" && (
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm w-1/4">
                      SubRisk
                    </th>
                  )}
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm w-1/2">
                    {category === "KYB" ? "Answer" : "Response"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {queries.map((query, idx) => {
                  let parsedResponse = null;
                  if (query.response) {
                    try {
                      const trimmed = query.response;
                      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
                        parsedResponse = JSON.parse(trimmed);
                        console.log("Parsed response:", parsedResponse);
                      }
                    } catch (e) {
                      console.error(
                        "JSON parse error on query.response:",
                        e,
                        query.response
                      );
                    }
                  }

                  return (
                    <tr
                      key={query.id}
                      className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors align-top"
                    >
                      {category === "KYB" && (
                        <td className="py-3 px-4 text-sm text-gray-800">
                          <p className="font-medium">{idx + 1}</p>
                        </td>
                      )}
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <p>
                          {category === "KYB" ? query.query : query?.riskType}
                        </p>
                      </td>
                      {category === "RISK_PARAMETERS" && (
                        <td className="py-3 px-4 text-sm text-gray-700">
                          <p>{query.description}</p>
                        </td>
                      )}
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <ChatMarkdown
                          msg={parsedResponse?.reasoning}
                          handleSourceClick={handleSourceClick}
                        />

                        <ReactMakrdown
                          components={{
                            a: ({ href, children }) => {
                              const match = href?.match(/=(\d+)$/);

                              const displayText = match ? match[1] : children;

                              return (
                                <button
                                  onClick={(e) =>
                                    handleSourceClick(e, href ?? "")
                                  }
                                  className="text-gray-600 text-xs font-thin cursor-pointer hover:underline"
                                >
                                  [{displayText}]
                                </button>
                              );
                            },
                          }}
                        >
                          {parsedResponse?.reasoning || ""}
                        </ReactMakrdown>

                        {/* {parsedResponse?.sources && parsedResponse.sources.length > 0 && (
            <span className="text-xs font-light text-gray-600">
              {parsedResponse.sources.map((source: any, i: number) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSourceClick(e, source.file_link);
                  }}
                  className="hover:underline hover:text-blue-700 cursor-pointer mr-1"
                >
                  [{source.page_number || 'Source'}]
                  {i !== parsedResponse.sources.length - 1 && ','}
                </button>
              ))}
            </span>
          )} */}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};

export default MlQueries;
